import {
  Button,
  Select,
  Space,
  SelectProps,
} from 'antd';
import { isEmpty } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getProductId, isElectron } from '@/infrastructure/product-info';
import { MatchMode } from '@/presentation/components/form/standards/types';
import { FieldScope } from '@/proto/field';
import { basicService, IIFilter } from '@/service/basic';
import styles from './index.module.scss';
import { AreaFieldValue, AreaNameEnum } from './types';

interface IOptions {
  value: string;
  label: string;
}

export interface TdsAreaEditorProps {
  value?: AreaFieldValue[];
  fieldScope?: FieldScope;
  onConfirm?: (value: AreaFieldValue[]) => void;
  onCancel?: () => void;
  /** 是否在挂载时立即加载候选项，默认 true */
  loadOnMount?: boolean;
}

export const TdsAreaEditor: React.FC<TdsAreaEditorProps> = (props) => {
  const { t } = useTranslation();
  const { value, fieldScope, onConfirm, onCancel, loadOnMount = true } = props;
  const selectPlaceholder = t('请选择');
  const orMatchOptions = [{ value: MatchMode.OR, label: t('是') }];

  const [countryOptions, setCountryOptions] = useState<IOptions[]>([]);
  const [provinceOptions, setProvinceOptions] = useState<IOptions[]>([]);
  const [cityOptions, setCityOptions] = useState<IOptions[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [customFilters, setCustomFilters] = useState<AreaFieldValue[]>(isEmpty(value) ? [] : (value ?? []));

  const clientCountry = isElectron()
    ? AreaNameEnum.ElectronClientCountry
    : AreaNameEnum.ClientCountry;
  const clientProvince = isElectron()
    ? AreaNameEnum.ElectronClientProvince
    : AreaNameEnum.ClientProvince;
  const clientCity = isElectron()
    ? AreaNameEnum.ElectronClientCity
    : AreaNameEnum.ClientCity;

  const getOptions = async (
    dim: string,
    filter?: IIFilter[],
  ): Promise<IOptions[]> => {
    const resp = await basicService.getMonitorFieldDistinctData({
      productID: getProductId(),
      dim,
      type: fieldScope as string,
      endTime: null,
      startTime: null,
      limit: 5000,
      optionalFilters: filter,
    });
    return resp?.map((v: string) => ({ value: v, label: v }));
  };

  const load = async () => {
    const list = await getOptions(clientCountry);
    setCountryOptions(list);
    const provinceValues = await getOptions(clientProvince);
    setProvinceOptions(provinceValues);
    const cityValues = await getOptions(clientCity);
    setCityOptions(cityValues);
    setLoaded(true);
  };

  useEffect(() => {
    if (loadOnMount && !loaded) {
      load();
    }
  }, [loadOnMount]);

  useEffect(() => {
    setCustomFilters(isEmpty(value) ? [] : (value ?? []));
  }, [value]);

  const countryChange: SelectProps['onChange'] = async (val) => {
    setCustomFilters([
      { name: clientCountry, operator: MatchMode.OR, values: val },
    ]);
    setProvinceOptions([]);
    setCityOptions([]);
    if (val.length) {
      const provinceValues = await getOptions(clientProvince, [
        { name: clientCountry, values: val, operator: MatchMode.OR },
      ]);
      const cityValues = await getOptions(clientCity, [
        { name: clientCountry, values: val, operator: MatchMode.OR },
      ]);
      setProvinceOptions(provinceValues);
      setCityOptions(cityValues);
    }
  };

  const provinceChange: SelectProps['onChange'] = async (val) => {
    setCustomFilters((list) => {
      const filterList = list.filter(item => item.name !== clientCity);
      if (list.find(item => item.name === clientProvince)) {
        return filterList.map(item => (item.name === clientProvince
          ? { name: clientProvince, operator: MatchMode.OR, values: val }
          : item));
      }
      return [
        ...filterList,
        { name: clientProvince, operator: MatchMode.OR, values: val },
      ];
    });
    setCityOptions([]);
    if (val.length) {
      const cityValues = await getOptions(clientCity, [
        { name: clientProvince, values: val, operator: MatchMode.OR },
      ]);
      setCityOptions(cityValues);
    }
  };

  const cityChange: SelectProps['onChange'] = async (val) => {
    setCustomFilters((list) => {
      if (list.find(item => item.name === clientCity)) {
        return list.map(item => (item.name === clientCity
          ? { name: clientCity, operator: MatchMode.OR, values: val }
          : item));
      }
      return [
        ...list,
        { name: clientCity, operator: MatchMode.OR, values: val },
      ];
    });
  };

  const handleConfirm = () => {
    onConfirm?.(customFilters?.filter(item => item?.values?.length) ?? []);
  };

  const handleCancel = () => {
    setCustomFilters(isEmpty(value) ? [] : (value ?? []));
    onCancel?.();
  };

  const countryValue = customFilters.find(item => item.name === clientCountry)?.values;
  const provinceValue = customFilters.find(item => item.name === clientProvince)?.values;
  const cityValue = customFilters.find(item => item.name === clientCity)?.values;

  return (
    <div className={styles.customOverlay}>
      <span>{t('以下的关系为全且')}</span>
      <div className={styles.itemContainer}>
        <div>
          <Space size={8}>
            <Select
              showArrow={false}
              open={false}
              value={t('国家/地区')}
              className={styles.column}
            />
            <Select
              options={orMatchOptions}
              value={MatchMode.OR}
              className={styles.operator}
              placeholder={selectPlaceholder}
            />
            <Select
              value={countryValue}
              mode="multiple"
              options={countryOptions}
              className={styles.values}
              placeholder={selectPlaceholder}
              onChange={countryChange}
            />
          </Space>
        </div>
        <div>
          <Space size={8}>
            <Select
              className={styles.column}
              value={t('省份')}
              showArrow={false}
              open={false}
            />
            <Select
              options={orMatchOptions}
              value={MatchMode.OR}
              className={styles.operator}
              placeholder={selectPlaceholder}
            />
            <Select
              value={provinceValue}
              mode="multiple"
              options={provinceOptions}
              className={styles.values}
              placeholder={selectPlaceholder}
              onChange={provinceChange}
            />
          </Space>
        </div>
        <div>
          <Space size={8}>
            <Select
              className={styles.column}
              value={t('城市')}
              showArrow={false}
              open={false}
            />
            <Select
              options={orMatchOptions}
              value={MatchMode.OR}
              className={styles.operator}
              placeholder={selectPlaceholder}
            />
            <Select
              value={cityValue}
              mode="multiple"
              options={cityOptions}
              className={styles.values}
              placeholder={selectPlaceholder}
              onChange={cityChange}
            />
          </Space>
        </div>
      </div>
      <div className={styles.footer}>
        <Space size={10}>
          <Button onClick={handleCancel}>{t('取消')}</Button>
          <Button type="primary" onClick={handleConfirm}>{t('确定')}</Button>
        </Space>
      </div>
    </div>
  );
};
